import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Button,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CallEndIcon from '@mui/icons-material/CallEnd';
import { useTranslation } from 'react-i18next';
import { RoomContext } from '@livekit/components-react';
import { CallTypeSelection, SimpleVoiceAssistant, OutboundCallStatus } from './components';
import { useRoomManager } from './hooks/useRoomManager';
import { useStyles } from './styles';
import { WebSocketCallComponent } from '@shared/components/WebSocketCallComponent';
import { ConfirmDialog } from '@shared/components/confirmDialog/ConfirmDialog';
import { useBeforeUnload, useBlocker, useConfirmDialog } from '@shared/hooks';
import { BillingAdmissionRecovery } from '@shared/components/billingAdmissionRecovery/BillingAdmissionRecovery';
import {
  billingAdmissionErrorMessage,
  billingErrorReason,
} from '@shared/utils/billingAdmissionError';

interface TestingAgentPopupProps {
  open: boolean;
  onClose: () => void;
  agentId: string;
  agentName: string;
  campaignId: string;
  companyHash: string;
  squadId?: string | null;
}

export const TestingAgentPopup: React.FC<TestingAgentPopupProps> = ({
  open,
  onClose,
  agentId,
  agentName,
  campaignId,
  companyHash,
  squadId,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [isCallEnded, setIsCallEnded] = useState(false);

  const handleBackendCallEnd = useCallback(() => setIsCallEnded(true), []);

  const {
    room,
    callType,
    currentCallType,
    phoneNumber,
    callId,
    onConnectButtonClicked,
    resetCallState,
    handleDisconnect,
    sipParticipantState,
    participantStates,
    createCallError,
  } = useRoomManager(agentId, campaignId, companyHash, onClose, handleBackendCallEnd, squadId);
  const [isWSCallInProgress, setIsWSCallInProgress] = useState(false);
  const [isWSCall, setIsWSCall] = useState<boolean>(false);
  const [isRoomConnected, setIsRoomConnected] = useState(false);
  const [isRoomConnecting, setIsRoomConnecting] = useState(false);
  const [isCallInitiated, setIsCallInitiated] = useState(false);
  const [isWSConnecting, setIsWSConnecting] = useState(false);
  const [callTypeSelectionResetToken, setCallTypeSelectionResetToken] = useState(0);
  const admissionReason = billingAdmissionErrorMessage(createCallError)
    ? billingErrorReason(createCallError)
    : null;

  // Track room connection state changes (including connecting state)
  useEffect(() => {
    const handleRoomStateChange = () => {
      const roomState = room.state;
      setIsRoomConnected(roomState === 'connected');
      setIsRoomConnecting(roomState === 'connecting');
    };

    handleRoomStateChange();
    room.on('connectionStateChanged', handleRoomStateChange);
    room.on('connected', handleRoomStateChange);
    room.on('disconnected', handleRoomStateChange);

    return () => {
      room.off('connectionStateChanged', handleRoomStateChange);
      room.off('connected', handleRoomStateChange);
      room.off('disconnected', handleRoomStateChange);
    };
  }, [room]);

  const isCallInProgress = isRoomConnected || isWSCallInProgress;
  const isCallStarting = isRoomConnecting || isWSConnecting;
  const shouldWarnOnExit = !isCallEnded && (isCallInProgress || isCallStarting);

  // Reset isCallInitiated when returning to call type selection
  useEffect(() => {
    if (
      (!callType && !isWSCallInProgress) ||
      (createCallError &&
        'status' in createCallError &&
        typeof createCallError.status === 'number' &&
        (createCallError.status === 409 ||
          createCallError.status === 400 ||
          createCallError.status >= 500))
    ) {
      setIsCallInitiated(false);
    }
  }, [callType, isWSCallInProgress, createCallError]);

  useBeforeUnload(shouldWarnOnExit);

  const { confirm: requestCloseConfirmation, dialogProps } = useConfirmDialog(
    open && shouldWarnOnExit,
  );

  useBlocker(open && shouldWarnOnExit, requestCloseConfirmation);

  useEffect(() => {
    if (!open) {
      setCallTypeSelectionResetToken((prev) => prev + 1);
    }
  }, [open]);

  const closePopup = () => {
    setIsCallEnded(false);

    if (isRoomConnected || isRoomConnecting) {
      handleDisconnect(); // This will call onClose after disconnecting
    } else {
      onClose(); // No active room call, just close (websocket call cleanup happens on unmount)
    }
  };

  const handleClose = () => {
    if (!shouldWarnOnExit) {
      closePopup();
      return;
    }

    void requestCloseConfirmation().then((confirmed) => {
      if (confirmed) closePopup();
    });
  };

  const handleStartNewCall = () => {
    setIsCallEnded(false);
    setIsWSCall(false);
    setIsWSCallInProgress(false);
    setIsCallInitiated(false);
    resetCallState();
  };

  return (
    <Dialog open={open} onClose={handleClose} className={classes.dialog} maxWidth="md" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h6" component="div">
          {t('agentPage.dialog.title')} - {agentName}
        </Typography>
        <IconButton aria-label="close" onClick={handleClose} className={classes.customizedButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContentFlex}>
        {admissionReason && !admissionReason.startsWith('execution_') && !isCallInProgress && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <BillingAdmissionRecovery reason={admissionReason} />
          </Alert>
        )}
        {isCallEnded && (
          <Box className={classes.callEndedContainer}>
            <Box className={classes.callEndedIconWrapper}>
              <CallEndIcon className={classes.callEndedIcon} />
            </Box>
            <Typography className={classes.callEndedTitle}>
              {t('agentPage.status.callEnded')}
            </Typography>
            <Typography className={classes.callEndedDescription}>
              {t('agentPage.status.callEndedDescription')}
            </Typography>
            <Box className={classes.callEndedActions}>
              <Button
                data-testid="ws-call-start-new"
                variant="contained"
                onClick={handleStartNewCall}
                className={classes.callEndedPrimaryButton}>
                {t('agentPage.dialog.startNewCall')}
              </Button>
              <Button
                variant="outlined"
                onClick={handleClose}
                className={classes.callEndedSecondaryButton}>
                {t('agentPage.dialog.close')}
              </Button>
            </Box>
          </Box>
        )}

        {!isCallEnded && !callType && !isWSCallInProgress && (
          <CallTypeSelection
            onStartCall={(
              callType: 'web-call' | 'outbound-phone-call',
              phoneNumber?: string,
              attributes?: Record<string, unknown>,
            ) => {
              setIsCallInitiated(true);
              onConnectButtonClicked(callType, phoneNumber, attributes);
            }}
            disableCallButton={isCallStarting || isCallInitiated}
            agentName={agentName}
            setIsWSCall={setIsWSCall}
            isSquadMode={!!squadId}
            resetToken={callTypeSelectionResetToken}
          />
        )}

        {!isCallEnded && callType === 'webCall' && (
          <div data-lk-theme="default" className={classes.roomContainer}>
            <RoomContext.Provider value={room}>
              <div className={classes.roomInner}>
                <SimpleVoiceAssistant
                  onConnectButtonClicked={() =>
                    onConnectButtonClicked(currentCallType, phoneNumber)
                  }
                  room={room}
                  agentName={agentName}
                  callId={callId}
                  participantStates={participantStates}
                />
              </div>
            </RoomContext.Provider>
          </div>
        )}

        {!isCallEnded && callType === 'outboundCall' && (
          <RoomContext.Provider value={room}>
            <OutboundCallStatus
              phoneNumber={phoneNumber}
              onClose={handleClose}
              agentName={agentName}
              callId={callId}
              sipParticipantState={sipParticipantState}
            />
          </RoomContext.Provider>
        )}

        {!isCallEnded && !callType && isWSCall && (
          <WebSocketCallComponent
            serviceUrl={import.meta.env.VITE_WORKER_API_URL}
            campaignId={campaignId}
            onCallEnd={() => setIsCallEnded(true)}
            setIsWSCallInProgress={setIsWSCallInProgress}
            setIsWSConnecting={setIsWSConnecting}
          />
        )}
      </DialogContent>
      <ConfirmDialog
        {...dialogProps}
        dialogTitle="agentPage.dialog.confirmCloseTitle"
        dialogContentText={t('agentPage.dialog.confirmCloseMessage')}
        variant="warning"
      />
    </Dialog>
  );
};
